import { FLOW_MODES } from '@avanta/shared/constants/appointments';
import { useFetchCenters } from '@avanta/shared/hooks/useFetchCenters';
import { useFetchSpecialitiesByPsycho } from '@avanta/shared/hooks/useFetchSpecialitiesByPsycho';
import { type Center, type Speciality, type Treatment } from '@avanta/shared/types/appointments';
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { AppointmentsReservePayload } from 'pages/AppointmentsReserve/constants';
import {
  type AppointmentsReserveFormData,
  type CenterOption,
} from 'pages/AppointmentsReserve/types';
import { useFetchTreatments } from './useFetchTreatments';

export const useFetchInsuredCenters = (): {
  values: {
    speciality: Speciality | undefined;
    treatment: Treatment | undefined;
    center: Center | undefined;
  };
  isLoading: boolean;
  centersOptions: CenterOption[];
} => {
  const { control } = useFormContext<AppointmentsReserveFormData>();

  const [contractId, patient, flow, psychologist, treatmentId, specialityBooked] = useWatch({
    control,
    name: [
      AppointmentsReservePayload.contractId,
      AppointmentsReservePayload.patient,
      AppointmentsReservePayload.flow,
      AppointmentsReservePayload.psychologist,
      AppointmentsReservePayload.psychologistServiceBooked,
      AppointmentsReservePayload.specialityBooked,
    ],
  });

  const patientId = patient?.id ?? '';
  const isSearchFlow = flow === FLOW_MODES.search_facultative;
  const psychologistId = isSearchFlow ? (psychologist?.id ?? '') : '';

  const { data: treatments, isLoading: isTreatmentsLoading } = useFetchTreatments();
  const treatment =
    flow === FLOW_MODES.edit
      ? treatments?.find(treatmentOption => treatmentOption.id === treatmentId)
      : treatments?.[0];
  const specialityId =
    (flow === FLOW_MODES.edit ? specialityBooked : treatment?.speciality.id) ?? '';

  const { data: psychoQuery, isLoading: isPsychoLoading } = useFetchSpecialitiesByPsycho({
    psychologistId,
    insurance: true,
    contractId,
    patientId,
  });

  const { data: centers, isLoading: isCentersLoading } = useFetchCenters(specialityId, {
    contractId,
    patientId,
  });
  const centersData = isSearchFlow ? psychoQuery?.workCenters : centers;
  const centersOptions = useMemo(
    () =>
      centersData?.map(center => ({
        value: String(center.id),
        label: center.name,
        description: `${center.address} ${center.postalCode} ${center.city}`,
        city: center.city,
      })) ?? [],
    [centersData]
  );

  const values = useMemo(
    () =>
      flow === FLOW_MODES.search_facultative
        ? {
            speciality: psychoQuery?.treatments?.[0]?.speciality,
            treatment: psychoQuery?.treatments?.[0],
            center: psychoQuery?.workCenters?.[0],
          }
        : {
            speciality: treatment?.speciality,
            treatment,
            center: centers?.[0],
          },
    [flow, psychoQuery, treatment, centers]
  );

  return {
    values,
    isLoading: isPsychoLoading || isTreatmentsLoading || isCentersLoading,
    centersOptions,
  };
};
